import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeetingPageResponse } from '@wundo/shared/meetingPage.ts';
import { fetchColleagues } from '@/lib/colleagues.ts';
import { deleteManualMeeting, updateManualMeeting } from '@/lib/meetings.ts';
import type { MeetingTypeOption } from '@/lib/withCurrentTemplate.ts';
import { ManualMeetingDeleteDialog } from './ManualMeetingDeleteDialog.tsx';
import { ManualMeetingDialog, type ManualMeetingValues } from './ManualMeetingDialog.tsx';
import type { MeetingDialog, MeetingDialogKind } from './meetingDialog.ts';
import { m } from '@/paraglide/messages.js';

export interface ManualMeetingDialogsProps {
  dialog: MeetingDialog | null;
  closeDialog: (kinds: MeetingDialogKind[]) => void;
  page: MeetingPageResponse;
  userId: string;
  meetingId: string;
  templates: MeetingTypeOption[];
  /* The meeting is gone: whoever put the view on screen takes it off. */
  onLeave?: () => void;
}

/**
 * Editing and deleting a meeting the user wrote down themselves. Each dialog
 * stays open and says so when its write fails, unlike the page's corrections,
 * which close every question on the way out.
 */
export function ManualMeetingDialogs({
  dialog,
  closeDialog,
  page,
  userId,
  meetingId,
  templates,
  onLeave,
}: ManualMeetingDialogsProps) {
  const queryClient = useQueryClient();
  const editing = dialog?.kind === 'edit';
  const invalidate = (queryKey: string[]) => queryClient.invalidateQueries({ queryKey });

  /* The names already known, suggested behind the "who is it with" field. */
  const { data: colleagues = [] } = useQuery({
    queryKey: ['colleagues', userId, 'active'],
    queryFn: () => fetchColleagues(userId, { hidden: false }),
    enabled: Boolean(userId) && editing,
  });

  const editMeeting = useMutation({
    mutationFn: (values: ManualMeetingValues) =>
      updateManualMeeting(userId, meetingId, {
        title: values.title,
        starts_at: values.startsAt,
        /* As the type menu stores it; rewriting the preparation is asked there. */
        template_id: values.templateId,
        /* The whole list, always: a name taken off is a name gone. */
        attendees: values.attendees.map((name) => ({ name })),
      }),
    onSuccess: async () => {
      closeDialog(['edit']);
      await invalidate(['meetingPage']);
      await invalidate(['meetings']);
      /* A name tagged here is a colleague from now on; the team page has to hear it. */
      await invalidate(['colleagues']);
    },
  });

  const removeMeeting = useMutation({
    mutationFn: () => deleteManualMeeting(userId, meetingId),
    /* Off the screen first: refetching it while shown answers with a load failure. */
    onSuccess: async () => {
      closeDialog(['delete']);
      onLeave?.();
      await invalidate(['meetings']);
      await invalidate(['meetingPage']);
    },
  });

  return (
    <>
      <ManualMeetingDialog
        open={editing}
        title={m.manual_meeting_edit()}
        saveLabel={m.manual_meeting_save()}
        initial={{
          title: page.meeting.title ?? '',
          startsAt: new Date(page.meeting.starts_at),
          templateId: page.template?.id ?? undefined,
          /* A manual meeting's guests are names only. */
          attendees: page.meeting.attendees.map((attendee) => attendee.label),
        }}
        templates={templates}
        withPeople={{ suggestions: colleagues.map((colleague) => colleague.label) }}
        pending={editMeeting.isPending}
        failed={editMeeting.isError}
        onSubmit={(values) => editMeeting.mutate(values)}
        onCancel={() => {
          closeDialog(['edit']);
          editMeeting.reset();
        }}
      />

      <ManualMeetingDeleteDialog
        open={dialog?.kind === 'delete'}
        pending={removeMeeting.isPending}
        failed={removeMeeting.isError}
        onConfirm={() => removeMeeting.mutate()}
        onCancel={() => {
          closeDialog(['delete']);
          removeMeeting.reset();
        }}
      />
    </>
  );
}
